<template>
  <div class="flex min-h-screen rounded-2xl m-1">
    <!-- Loading Indicator -->
    <div
      v-if="loading"
      class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-50"
    >
      <LoadingIndicator 
        title="Loading query details..."
        subtitle="Fetching query information and metadata"
        status="Processing request"
      />
    </div>

    <!-- Toast Notification -->
    <div
      v-if="showToast"
      class="fixed bottom-8 left-1/2 transform -translate-x-1/2 bg-[#157813] text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2 transition-opacity duration-300 z-50"
    >
      <svg
        class="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M5 13l4 4L19 7"
        ></path>
      </svg>
      <span>Copied to clipboard!</span>
    </div>

    <div class="container mx-auto px-4 py-6 w-full overflow-x-auto">
      <div class="flex flex-col md:flex-row gap-4 w-full">
        <!-- Sidebar -->
        <aside
          class="md:w-72 w-full flex-shrink-0 bg-gray-100 rounded-lg shadow-sm border-gray-200 p-4 md:p-5 md:mr-10"
        >
          <h2 class="text-xl font-bold text-[#157813] mb-6">Navigation</h2>
          <ul class="space-y-4 text-black">
            <!-- Regular navigation items -->
            <li>
              <a
                href="#metadata"
                class="flex items-center space-x-2 hover:text-[#33dd1a]"
                @click.prevent="scrollToSection('metadata')"
              >
                <Database class="w-5 h-5" /> <span>Metadata</span>
              </a>
            </li>
            <li>
              <a
                href="#sql"
                class="flex items-center space-x-2 hover:text-[#33dd1a]"
                @click.prevent="scrollToSection('sql')"
              >
                <FileText class="w-5 h-5" /> <span>SQL Text</span>
              </a>
            </li>

            <li>
              <a
                href="#description"
                class="flex items-center space-x-2 hover:text-[#33dd1a]"
                @click.prevent="scrollToSection('description')"
              >
                <Book class="w-5 h-5" /> <span>Description</span>
              </a>
            </li>

            <!-- HERE: link to #rules actually works now -->
            <li>
              <a
                href="#rules"
                class="flex items-center space-x-2 hover:text-[#33dd1a]"
                @click.prevent="scrollToSection('rules')"
              >
                <ShieldCheck class="w-5 h-5" /> <span>Rules</span>
              </a>
            </li>

            <li>
              <a
                href="#features"
                class="flex items-center space-x-2 hover:text-[#33dd1a]"
                @click.prevent="scrollToSection('features')"
              >
                <Settings class="w-5 h-5" /> <span>Features</span>
              </a>
            </li>
            <li>
              <a
                href="#Inferences"
                class="flex items-center space-x-2 hover:text-[#33dd1a]"
                @click.prevent="scrollToSection('Inferences')"
              >
                <ClipboardList class="w-5 h-5" /> <span>Inferred Tags</span>
              </a>
            </li>
            <li>
              <a
                href="#mentioned"
                class="flex items-center space-x-2 hover:text-[#33dd1a]"
                @click.prevent="scrollToSection('mentioned')"
              >
                <Tags class="w-5 h-5" /> <span>Mentioned Tags</span>
              </a>
            </li>
            <li v-if="showRelationships">
              <a
                href="#query-relationships"
                class="flex items-center space-x-2 hover:text-[#33dd1a]"
                @click.prevent="scrollToSection('query-relationships')"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 3v18h18" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 12v6" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v10" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 4v14" />
                </svg>
                <span>Query Graph</span>
              </a>
            </li>



          </ul>
        </aside>

        <!-- Main Content -->
        <main class="w-full lg:w-3/4 shadow-xl md:px-8 px-4 py-6 relative">
          <!-- Dropdown Button -->
          <div class="absolute top-2 right-3">
            <div class="relative">
              <button
                @click="toggleDropdown"
                class="bg-[#157813] hover:bg-[#3c8c2d] text-white rounded-xl font-bold px-3 py-2 md:mx-4 mb-12"
              >
                PDF/CSV
              </button>
              <div
                v-if="isDropdownOpen"
                class="absolute right-0 mt-2 w-48 bg-white shadow-md rounded-lg z-10"
              >
                <button
                  @click="downloadPdf"
                  class="block w-full text-left px-4 py-2 hover:bg-gray-100 text-black"
                >
                  Download as PDF
                </button>
                <button
                  @click="exportToCsv"
                  class="block w-full text-left px-4 py-2 hover:bg-gray-100 text-black"
                >
                  Export Query to CSV
                </button>
              </div>
            </div>
          </div>

          <div id="content" class="m-6">
            <!-- Title Section -->
            <header class="mb-8">
              <h1 class="text-4xl font-bold text-black">
                Query ID: {{ query?.id }}
              </h1>
              <p class="text-gray-500 text-sm mt-2 mx-3">
                Query Type: {{ getQueryTypeLabel(query?.componentType) }}
              </p>
              <p class="text-gray-500 text-sm mt-2 mx-3">
                Page Last Updated: {{ query?.RunTimestamp }}
              </p>
            </header>

            <!-- Metadata Section -->
            <section id="metadata" class="mb-8">
              <h2 class="text-2xl font-semibold text-[#157813] mb-4">
                Metadata
              </h2>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div
                  class="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                >
                  <h3 class="font-bold text-black">
                    <strong>Author:</strong> {{ query?.author }}
                  </h3>
                </div>
                <div
                  class="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                >
                  <h3 class="font-bold text-black">
                    <strong>Start Date:</strong>
                    {{ query?.queryStartTime ? query?.startDate : "N/A" }}
                  </h3>
                </div>
                <div
                  class="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                >
                  <h3 class="font-bold text-black">
                    <strong>Run Date:</strong> N/A
                  </h3>
                </div>
                <div
                  class="bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
                >
                  <h3 class="font-bold text-black">
                    <strong>Run Count:</strong>
                    {{ query?.extraction  }}
                  </h3>
                </div>
              </div>
            </section>

            <!-- SQL Section -->
            <section id="sql" class="mb-8">
              <h2 class="text-2xl font-semibold text-[#157813] mb-4">
                SQL Text
              </h2>
              <!-- Container for pre and copy button -->
              <div class="relative">
                <pre
                  class="font-code w-full bg-gray-100 px-6 py-10 rounded border border-gray-200 text-sm text-black"
                  style="
                    white-space: pre-wrap;
                    word-break: break-word;
                    overflow-x: hidden;
                  "
                  v-html="formattedSQL"
                ></pre>
                <!-- Copy button -->
                <button
                  class="absolute top-2 right-2 bg-white border border-gray-300 rounded p-1 shadow hover:bg-gray-50"
                  @click="copyQueryWithTimestamp"
                  title="Copy query text, metadata, and timestamp"
                >
                  <!-- SVG icon -->
                  <svg
                    viewBox="0 0 24 24"
                    class="w-6 h-6 inline hover:fill-[#1aa818]"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <g stroke-width="0"></g>
                    <g stroke-linecap="round" stroke-linejoin="round"></g>
                    <g>
                      <path
                        d="M15.24 2H11.3458C9.58159 1.99999 8.18418 1.99997 7.09054 2.1476C5.96501 2.29953 5.05402 2.61964 4.33559 3.34096C3.61717 4.06227 3.29833 4.97692 3.14701 6.10697C2.99997 7.205 2.99999 8.60802 3 10.3793V16.2169C3 17.725 3.91995 19.0174 5.22717 19.5592C5.15989 18.6498 5.15994 17.3737 5.16 16.312L5.16 11.3976L5.16 11.3024C5.15993 10.0207 5.15986 8.91644 5.27828 8.03211C5.40519 7.08438 5.69139 6.17592 6.4253 5.43906C7.15921 4.70219 8.06404 4.41485 9.00798 4.28743C9.88877 4.16854 10.9887 4.1686 12.2652 4.16867L12.36 4.16868H15.24L15.3348 4.16867C16.6113 4.1686 17.7088 4.16854 18.5896 4.28743C18.0627 2.94779 16.7616 2 15.24 2Z"
                        fill="#157813"
                      ></path>
                      <path
                        d="M6.6001 11.3974C6.6001 8.67119 6.6001 7.3081 7.44363 6.46118C8.28716 5.61426 9.64481 5.61426 12.3601 5.61426H15.2401C17.9554 5.61426 19.313 5.61426 20.1566 6.46118C21.0001 7.3081 21.0001 8.6712 21.0001 11.3974V16.2167C21.0001 18.9429 21.0001 20.306 20.1566 21.1529C19.313 21.9998 17.9554 21.9998 15.2401 21.9998H12.3601C9.64481 21.9998 8.28716 21.9998 7.44363 21.1529C6.6001 20.306 6.6001 18.9429 6.6001 16.2167V11.3974Z"
                        fill="#157813"
                      ></path>
                    </g>
                  </svg>
                </button>
              </div>
            </section>

            <!-- Description Section -->
            <section id="description" class="mb-8">
              <h2 class="text-2xl font-semibold text-[#157813] mb-4">
                Description
              </h2>
              <p class="text-black">{{ query?.description?.main }}</p>
            </section>

            <!-- RULES Section (formerly in description) -->
            <section id="rules" class="mb-8">
              <h2 class="text-2xl font-semibold text-[#157813] mb-4">Rules</h2>
              <ul class="mt-4 text-black space-y-4">
                <li>
                  <strong>Inclusion Rules:</strong>
                  <ul class="mt-2 pl-4 list-disc">
                    <li
                      v-if="formattedInclusionRules.length > 0"
                      v-for="(rule, index) in formattedInclusionRules"
                      :key="'inclusion-' + index"
                    >
                      {{ rule }}
                    </li>
                    <li v-else>N/A</li>
                  </ul>
                </li>
                <li>
                  <strong>Exclusion Rules:</strong>
                  <ul class="mt-2 pl-4 list-disc">
                    <li
                      v-if="formattedExclusionRules.length > 0"
                      v-for="(rule, index) in formattedExclusionRules"
                      :key="'exclusion-' + index"
                    >
                      {{ rule }}
                    </li>
                    <li v-else>N/A</li>
                  </ul>
                </li>
              </ul>
            </section>

            <!-- Features Section -->
            <section
              id="features"
              class="mb-8 p-6 bg-white rounded-xl shadow-md"
            >
              <div class="flex items-center gap-3 mb-6">
                <h2 class="text-2xl font-semibold text-[#157813]">Features</h2>
                <div class="relative group">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-gray-400 cursor-help"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8.228 9c.549-1.165 2.03-2 3.772-2 
                        2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093
                        m0 3h.01M12 21a9 9 0 100-18 9 9 0 000 18z"
                    />
                  </svg>
                  <div
                    class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-4 py-2 bg-black rounded-lg invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-300 w-64 z-10"
                  >
                    <p class="text-white text-sm">
                      Numerical calculations, attributes, groupings explicitly
                      typed by the user
                    </p>
                    <div
                      class="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-black rotate-45"
                    ></div>
                  </div>
                </div>
              </div>

              <div v-if="query?.features && query.features.length > 0">
                <div class="flex flex-wrap gap-3">
                  <div
                    v-for="tag in query.features"
                    :key="tag"
                    class="relative group"
                  >
                    <span
                      class="relative overflow-hidden rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300 hover:shadow-lg hover:scale-105 hover:-translate-y-0.5 cursor-pointer transform border-gray-300 bg-gray-50 text-black hover:ring-2 hover:ring-opacity-50 hover:ring-gray-300"
                      @click="goToFeatureScreen(tag)"
                    >
                      <span class="relative z-10">
                        {{ tag }}
                      </span>
                    </span>
                  </div>
                </div>
              </div>
              <div v-else class="text-gray-500">N/A</div>
            </section>

            <!-- Inferred Tags Section -->
            <section
              id="Inferences"
              class="mb-8 p-6 bg-white rounded-xl shadow-md"
            >
              <div class="flex items-center gap-3 mb-6">
                <h2 class="text-2xl font-semibold text-[#157813]">
                  Inferred Tags
                </h2>
                <div class="relative group">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-gray-500 cursor-help"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8.228 9c.549-1.165 
                        2.03-2 3.772-2 2.21 0 4 1.343 4 3 
                        0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093
                        m0 3h.01M12 21a9 9 0 100-18 9 9 0 000 18z"
                    />
                  </svg>
                  <div
                    class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity w-64 text-center"
                  >
                    Automatically inferred tags based on query analysis
                    <div
                      class="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45"
                    ></div>
                  </div>
                </div>
              </div>

              <div v-if="groupedInferred.length > 0">
                <div
                  v-for="group in groupedInferred"
                  :key="group.category + group.tags.join('-')"
                  class="mb-6"
                >
                  <template v-if="group.category">
                    <h3 class="text-lg font-semibold text-gray-700 mb-3">
                      {{ group.category }}
                    </h3>
                  </template>
                  <div class="flex flex-wrap gap-3">
                    <div
                      v-for="tag in group.tags"
                      :key="tag"
                      class="relative group"
                    >
                      <span
                        :class="getTagClassByCategory(group.category)"
                        @click="goToInferenceScreen(tag)"
                        class="inline-flex items-center"
                      >
                        <span class="relative z-10">
                          {{ tag }}
                        </span>
                      </span>
                      <div
                        v-if="group.category"
                        class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-300 z-10 whitespace-nowrap"
                      >
                        {{ group.category }}
                        <div
                          class="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-gray-500">N/A</div>
            </section>

            <!-- Mentioned Tags Section -->
            <section
              id="mentioned"
              class="mb-8 p-6 bg-white rounded-xl shadow-md"
            >
              <div class="flex items-center gap-3 mb-6">
                <h2 class="text-2xl font-semibold text-[#157813]">
                  Mentioned Tags
                </h2>
                <div class="relative group">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="h-5 w-5 text-gray-500 cursor-help"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M8.228 9c.549-1.165 
                        2.03-2 3.772-2 2.21 0 4 1.343 4 3 
                        0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093
                        m0 3h.01M12 21a9 9 0 100-18 9 9 0 000 18z"
                    />
                  </svg>
                  <div
                    class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-300 w-64 text-center"
                  >
                    Direct mentions of tables, fields, or conditions
                    <div
                      class="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45"
                    ></div>
                  </div>
                </div>
              </div>

              <div v-if="groupedMentioned.length > 0">
                <div
                  v-for="group in groupedMentioned"
                  :key="group.category + group.tags.join('-')"
                  class="mb-6"
                >
                  <template v-if="group.category">
                    <h3 class="text-lg font-semibold text-gray-700 mb-3">
                      {{ group.category }}
                    </h3>
                  </template>
                  <div class="flex flex-wrap gap-3">
                    <div
                      v-for="tag in group.tags"
                      :key="tag"
                      class="relative group"
                    >
                      <span
                        :class="getTagClassByCategory(group.category)"
                        @click="goToMentionScreen(tag)"
                        class="inline-flex items-center"
                      >
                        <span class="relative z-10">
                          {{ tag }}
                        </span>
                      </span>
                      <div
                        v-if="group.category"
                        class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity duration-300 z-10 whitespace-nowrap"
                      >
                        {{ group.category }}
                        <div
                          class="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-gray-900 rotate-45"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="text-gray-500">N/A</div>
            </section>

            <!-- Query Relationships Section -->
            <section id="query-relationships" v-if="showRelationships" class="mb-8">
              <h2 class="text-2xl font-semibold text-[#157813] mb-4">
                Query Relationships
              </h2>
              <div class="bg-white border border-gray-200 rounded-xl p-6 shadow-md">
                <QueryFlowGraph 
                  :queries="queries"
                  :current-query-id="query?.id || ''"
                />
              </div>
            </section>

            <!-- Usage Statistics Section -->
            <section id="usage-stats" class="mt-8">
              <h2 class="text-2xl font-semibold text-[#157813] mb-4">
                Usage Statistics
              </h2>
              <p class="text-black">Section in progress..</p>
            </section>
          </div>
        </main>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import LoadingIndicator from "@/components/LoadingIndicator.vue";
import QueryFlowGraph from "@/components/QueryFlowGraph.vue";
import SuggestionDropdown from "@/components/SuggestionDropdown.vue";
import { formatSQL } from "@/formatSQL";
import { Query, transformQueries } from "@/models/Query";
import rawQueries from "@/queries.json";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  Book,
  ClipboardList,
  Database,
  FileText,
  Settings,
  ShieldCheck,
  Tags,
} from "lucide-vue-next";
import Prism from "prismjs";
import "prismjs/components/prism-sql";
import "prismjs/themes/prism.css";
import { computed, defineComponent, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";

export default defineComponent({
  name: "QueryDetail",
  components: {
    Database,
    FileText,
    Book,
    ShieldCheck,
    Settings,
    Tags,
    ClipboardList,
    QueryFlowGraph,
    LoadingIndicator,
    SuggestionDropdown,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const loading = ref(false);
    const showToast = ref(false);

    // Transform your raw queries JSON to typed queries
    const queries = transformQueries(rawQueries as any[]);

    // Create a reactive query ref
    const query = ref<Query | null>(null);

    // Function to find and set the current query
    function findAndSetQuery(queryId: string) {
      if (!queryId) return;
      
      const foundQuery = queries.find((q) => q.id === queryId);
      if (foundQuery) {
        query.value = foundQuery;
      }
      loading.value = false;
    }

    // Function to get human-readable query type label
    function getQueryTypeLabel(componentType: string | undefined): string {
      if (!componentType) return "Unknown";
      
      switch (componentType.toUpperCase()) {
        case "MAIN":
          return "Original Query";
        case "CTE":
        case "JOIN":
        case "FROM":
        case "SUBQUERY":
          return "Extracted Neuron";
        default:
          return componentType;
      }
    }

    // Watch for route changes
    watch(
      () => route.params.id,
      (newId) => {
        if (newId) {
          loading.value = true;
          findAndSetQuery(newId as string);
        }
      },
      { immediate: true }
    );

    // Navigation function
    function goToQuery(queryId: string) {
      if (!queryId) return;
      loading.value = true;
      router.push(`/query/${queryId}`);
    }

    // --------------------------------------------------
    // PARENT/CHILD LOGIC
    // --------------------------------------------------
    const parent = computed(() => {
      if (!query.value) return null;
      return queries.find((q) => q.id === query.value?.parentComponent);
    });

    const children = computed(() => {
      if (!query.value) return [];
      return queries.filter((q) => q.parentComponent === query.value?.id);
    });

    // Get grandparents (parents of the parent)
    const grandparents = computed(() => {
      if (!parent.value) return [];
      return queries.filter((q) => q.id === parent.value?.parentComponent);
    });

    // Get grandchildren (children of children)
    const grandchildren = computed(() => {
      if (!children.value.length) return [];
      const grandchildQueries: Query[] = [];
      children.value.forEach((child) => {
        const childChildren = queries.filter(
          (q) => q.parentComponent === child.id
        );
        grandchildQueries.push(...childChildren);
      });
      return grandchildQueries;
    });

    const showRelationships = computed(() => {
      return (
        parent.value !== null ||
        children.value.length > 0 ||
        grandparents.value.length > 0 ||
        grandchildren.value.length > 0
      );
    });

    function grandparentX(index: number) {
      const totalGrandparents = grandparents.value.length;
      const spacing = 400; // Consistent spacing between nodes
      const totalWidth = (totalGrandparents - 1) * spacing;
      const startX = 600 - totalWidth / 2;
      return startX + index * spacing;
    }

    function childX(index: number) {
      const totalChildren = children.value.length;
      if (totalChildren === 1) {
        return 1200; // Center position for single child
      }
      // For multiple children, calculate diagonal positions
      const spacing = 800; // Wide spacing for diagonal layout
      if (index === 0) {
        return 800; // First child goes to left
      }
      return 1600; // Second child goes to right
    }

    function grandchildX(index: number) {
      const totalGrandchildren = grandchildren.value.length;
      const spacing = 400; // Same spacing as other levels
      const totalWidth = (totalGrandchildren - 1) * spacing;
      const startX = 600 - totalWidth / 2;
      return startX + index * spacing;
    }

    function grandchildParentX(index: number) {
      const grandchild = grandchildren.value[index];
      const parent = children.value.find((child) =>
        queries.some(
          (q) => q.id === grandchild.id && q.parentComponent === child.id
        )
      );
      if (!parent) return 500;
      const childIndex = children.value.findIndex((c) => c.id === parent.id);
      return childX(childIndex);
    }

    // --------------------------------------------------
    // Navigation / Helper methods
    // --------------------------------------------------
    function scrollToSection(sectionId: string) {
      const el = document.getElementById(sectionId);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }

    const goToMentionScreen = (mention: string) => {
      router.push({ name: "QueryByMention", query: { mention } });
    };
    const goToFeatureScreen = (feature: string) => {
      router.push({ name: "QueryByFeature", query: { feature } });
    };
    const goToInferenceScreen = (inference: string) => {
      router.push({ name: "QueryByInference", query: { inference } });
    };

    // --------------------------------------------------
    // Tag Grouping Logic for color-coding
    // --------------------------------------------------
    function getTagCategory(tag: string): string {
      if (query.value?.indicationEnrichments.includes(tag)) return "Indication";
      if (query.value?.medicationEnrichments.includes(tag)) return "Medication";
      if (query.value?.procedureEnrichments.includes(tag)) return "Procedure";
      if (query.value?.tableNames.includes(tag)) return "Table";
      if (query.value?.columnNames.includes(tag)) return "Column";
      return "Other";
    }

    // Group tags (Inferred, Mentioned, or Features) by category
    function groupAndSortTags(tags: string[]) {
      const groups: Record<string, string[]> = {
        Indication: [],
        Medication: [],
        Procedure: [],
        Table: [],
        Column: [],
        "Time Period": [],
        Other: [],
      };

      tags.forEach((tag) => {
        const category = getTagCategory(tag);
        groups[category].push(tag);
      });

      // Sort each category's tags
      Object.values(groups).forEach((catTags) => catTags.sort());

      // The order in which categories appear
      const orderedCategories = [
        "Indication",
        "Medication",
        "Procedure",
        "Table",
        "Column",
        "Time Period",
        "Other", // match the fallback in groups
      ];

      return orderedCategories
        .filter((category) => groups[category].length > 0)
        .map((category) => ({
          category,
          tags: groups[category],
        }));
    }

    const groupedFeatures = computed(() =>
      groupAndSortTags(query.value?.features || [])
    );
    const groupedInferred = computed(() =>
      groupAndSortTags(query.value?.inferences || [])
    );
    const groupedMentioned = computed(() =>
      groupAndSortTags(query.value?.mentioned || [])
    );

    // Assign a color style based on category
    function getTagClassByCategory(category: string) {
      const base =
        "relative overflow-hidden rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300 hover:shadow-lg hover:scale-105 hover:-translate-y-0.5 cursor-pointer transform";
      const hover = "hover:ring-2 hover:ring-opacity-50";

      switch (category) {
        case "Indication":
          return `${base} border-green-400 bg-green-50 text-green-700 ${hover} hover:ring-green-200`;
        case "Medication":
          return `${base} border-yellow-400 bg-yellow-50 text-yellow-700 ${hover} hover:ring-yellow-200`;
        case "Procedure":
          return `${base} border-red-400 bg-red-50 text-red-700 ${hover} hover:ring-red-200`;
        case "Table":
          return `${base} border-purple-400 bg-purple-50 text-purple-700 ${hover} hover:ring-purple-200`;
        case "Column":
          return `${base} border-orange-400 bg-orange-50 text-orange-700 ${hover} hover:ring-orange-200`;
        case "Time Period":
          return `${base} border-blue-400 bg-blue-50 text-blue-700 ${hover} hover:ring-blue-200`;
        case "Other":
          return `${base} border-gray-300 bg-gray-50 text-black ${hover} hover:ring-gray-300`;

        default:
          return `${base} border-green-200 bg-green-50 text-black ${hover} hover:ring-gray-200`;
      }
    }

    // --------------------------------------------------
    // Inclusion & Exclusion Rules
    // --------------------------------------------------
    function parseRulesAsText(rules: string) {
      // Handle empty or "None" rules
      if (!rules || rules.trim() === "" || rules.trim() === "None") {
        return [];
      }
      
      // Try different regex patterns for different rule formats
      const patterns = [
        // Format 1: "Rule X: Column Name: ..."
        /(Rule\s\d+:\sColumn Name:\s[^,]+,\sFilter Condition:\s[\s\S]+?(?:\(.+?\))?)(?=Rule\s\d+:|$)/g,
        // Format 2: "X. Column Name: ..."
        /(\d+\.\sColumn Name:\s[^,]+,\sFilter Condition:\s[\s\S]+?(?:\(.+?\))?)(?=\d+\.|$)/g,
        // Format 3: "- Column Name: ..."
        /(-\sColumn Name:\s[^,]+,\sFilter Condition:\s[\s\S]+?(?:\(.+?\))?)(?=-\s|$)/g
      ];
      
      const matches = [];
      
      // Try each pattern
      for (const pattern of patterns) {
        let match;
        while ((match = pattern.exec(rules)) !== null) {
          // Clean up the rule text by removing leading dashes
          let ruleText = match[1].trim();
          if (ruleText.startsWith('-')) {
            ruleText = ruleText.substring(1).trim();
          }
          matches.push(ruleText);
        }
        
        // If we found matches with this pattern, no need to try others
        if (matches.length > 0) {
          break;
        }
      }
      
      return matches;
    }

    const formattedInclusionRules = parseRulesAsText(
      query.value?.description.inclusionRules || ""
    );
    const formattedExclusionRules = parseRulesAsText(
      query.value?.description.exclusionRules || ""
    );
    const isHovered = ref<string | null>(null);

    function hasGrandchildren(childId: String) {
      return grandchildren.value.some((gc) => {
        const childOfChild = queries.find((q) => q.id === gc.id);
        return childOfChild && childOfChild.parentComponent === childId;
      });
    }

    // --------------------------------------------------
    // Download / Export Logic
    // --------------------------------------------------
    const isDropdownOpen = ref(false);
    function toggleDropdown() {
      isDropdownOpen.value = !isDropdownOpen.value;
    }

    async function downloadPdf() {
      loading.value = true;
      try {
        const content = document.getElementById("content");
        if (!content) throw new Error("Content element not found.");

        const tempContainer = document.createElement("div");
        tempContainer.style.position = "absolute";
        tempContainer.style.top = "-9999px";
        tempContainer.style.left = "-9999px";
        tempContainer.style.width = "100%";
        tempContainer.style.background = "white";
        document.body.appendChild(tempContainer);

        // Clone the content
        const clonedContent = content.cloneNode(true) as HTMLElement;

        // Example header
        const header = document.createElement("nav");
        header.className = "bg-black text-white rounded-xl mb-4";
        header.innerHTML = `
          <div class="flex items-center justify-center p-4">
            <div class="font-bold capitalize flex flex-col justify-center items-center text-center">
              <strong class="text-2xl block text-center">Neuron</strong>
              <span class="text-lg font-normal block mt-2">The brain of your analytics workflow</span>
            </div>
          </div>
        `;
        clonedContent.prepend(header);
        tempContainer.appendChild(clonedContent);

        const canvas = await html2canvas(tempContainer, { scale: 2 });
        const imgData = canvas.toDataURL("image/png");
        const pdf = new jsPDF({
          orientation: "portrait",
          unit: "px",
          format: [canvas.width + 50, canvas.height],
        });

        pdf.addImage(imgData, "PNG", 25, 10, canvas.width, canvas.height);
        pdf.save(`query_${query.value?.id}.pdf`);
      } catch (error) {
        console.error("Error generating PDF:", error);
      } finally {
        loading.value = false;
      }
    }

    function exportToCsv() {
      loading.value = true;
      try {
        const csvContent = [
          [
            "start_date",
            "end_date",
            "Component Type",
            "Component ID",
            "AST_Structure",
            "clusterId",
            "Description",
            "Inclusion Rules",
            "Exclusion Rules",
            "Mentioned",
            "Inferred",
            "Features",
          ],
          [
            query.value?.queryStartTime || "N/A",
            "N/A",
            query.value?.queryLabel || "N/A",
            query.value?.id || "N/A",
            query.value?.neuronText || "N/A",
            query.value?.clusterId || "N/A",
            query.value?.description.main || "N/A",
            formattedInclusionRules.join("; ") || "None",
            formattedExclusionRules.join("; ") || "None",
            query.value?.tags ? query.value.tags.join("; ") : "N/A",
            query.value?.inferences ? query.value.inferences.join("; ") : "N/A",
            query.value?.features ? query.value.features.join("; ") : "N/A",
          ],
        ];

        const csvString = csvContent
          .map((row) =>
            row
              .map((cell) => {
                if (
                  typeof cell === "string" &&
                  (cell.includes(",") ||
                    cell.includes('"') ||
                    cell.includes("\n"))
                ) {
                  return `"${cell.replace(/"/g, '""')}"`;
                }
                return cell;
              })
              .join(",")
          )
          .join("\n");

        const blob = new Blob([csvString], {
          type: "text/csv;charset=utf-8;",
        });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `query_${query.value?.clusterId}.csv`;
        link.click();
      } catch (error) {
        console.error("Error exporting CSV:", error);
      } finally {
        loading.value = false;
        isDropdownOpen.value = false;
      }
    }

    // --------------------------------------------------
    // Copy with Timestamp
    // --------------------------------------------------
    function copyQueryWithTimestamp() {
      const currentTime = new Date().toLocaleString();
      const meta = `
Author: ${query.value?.author}
Start Date: ${query.value?.queryStartTime ? query.value.startDate : "N/A"}
Run Count: ${query.value?.extraction ?? "N/A"}

SQL:
${query.value?.neuronText}

Copied at: ${currentTime}
      `.trim();

      navigator.clipboard
        .writeText(meta)
        .then(() => {
          showToast.value = true;
          setTimeout(() => {
            showToast.value = false;
          }, 2000);
        })
        .catch((err) => {
          console.error("Failed to copy text: ", err);
        });
    }

    // --------------------------------------------------
    // Prism + Formatted SQL
    // --------------------------------------------------
    const formattedSQL = computed(() =>
      formatSQL(query.value?.neuronText || "")
    );

    // We highlight on mount if desired
    onMounted(() => {
      Prism.highlightAll(); // Ensures Prism applies highlighting after DOM renders
    });
    // Sorting / filtering watchers
    function applyFilters() {
      // ... not used in your snippet, but you can place watchers here
    }

    const siblings = computed(() => {
      if (!query.value?.id || !parent.value?.id) return [];
      return queries.filter(q => 
        q.parentComponent === parent.value?.id && 
        q.id !== query.value?.id
      );
    });

    function hasChildren(nodeId: string) {
      return queries.some(q => q.parentComponent === nodeId);
    }

    // Add this computed property to sort siblings
    const sortedSiblings = computed(() => {
      if (!query.value?.id) return { left: [], right: [] };
      
      const left: Query[] = [];
      const right: Query[] = [];
      
      siblings.value.forEach(sibling => {
        const currentId = query.value?.id?.split('-')[1] ?? '0';
        const siblingId = sibling.id.split('-')[1];
        
        if (parseInt(siblingId) < parseInt(currentId)) {
          left.push(sibling);
        } else {
          right.push(sibling);
        }
      });
      
      return {
        left: left.sort((a, b) => parseInt(a.id.split('-')[1]) - parseInt(b.id.split('-')[1])),
        right: right.sort((a, b) => parseInt(a.id.split('-')[1]) - parseInt(b.id.split('-')[1]))
      };
    });

    function getSiblingChildren(siblingId: string) {
      return queries.filter(q => q.parentComponent === siblingId);
    }

    return {
      query,
      loading,
      showToast,
      isDropdownOpen,
      parent,
      children,
      showRelationships,
      isHovered,
      hasGrandchildren,
      queries,
      // Nav
      scrollToSection,
      toggleDropdown,
      downloadPdf,
      exportToCsv,
      copyQueryWithTimestamp,

      // Tag grouping
      groupedFeatures,
      groupedInferred,
      groupedMentioned,
      getTagClassByCategory,

      // Query type label
      getQueryTypeLabel,

      // "Rules" extracted from description
      formattedInclusionRules,
      formattedExclusionRules,

      // For parent/child display
      goToQuery,
      grandparentX,
      childX,
      grandchildX,
      grandchildParentX,

      // Additional nav
      goToMentionScreen,
      goToFeatureScreen,
      goToInferenceScreen,

      formattedSQL,
      grandparents,
      grandchildren,
      siblings,
      hasChildren,
      sortedSiblings,
      getSiblingChildren,
    };
  },
});
</script>

<style scoped>
/* Custom scrollbar styles */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}
::-webkit-scrollbar-thumb {
  background: #157813;
  border-radius: 3px;
}
::-webkit-scrollbar-thumb:hover {
  background: #46cc44;
}

/* Graph specific styles */
.graph-container {
  background: linear-gradient(to bottom, rgba(255,255,255,0.9), rgba(255,255,255,0.7));
  border-radius: 1rem;
  padding: 1rem;
}

/* Node styles */
.graph-container .rounded-full {
  transition: all 0.3s ease;
  position: relative;
  z-index: 2;
}

.graph-container .rounded-full:hover {
  transform: scale(1.1);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

/* Connection line styles */
.graph-container .bg-gradient-to-b,
.graph-container .bg-gradient-to-r {
  position: relative;
  z-index: 1;
}

/* Enhanced connection animations */
@keyframes pulseConnection {
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
}

.graph-container .bg-gradient-to-b,
.graph-container .bg-gradient-to-r {
  animation: pulseConnection 2s infinite;
}

/* Node hover effects */
.graph-container .rounded-full::after {
  content: '';
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border-radius: 50%;
  background: rgba(21, 120, 19, 0.1);
  z-index: -1;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.graph-container .rounded-full:hover::after {
  opacity: 1;
}

/* Connection line hover effects */
.graph-container .bg-gradient-to-b:hover,
.graph-container .bg-gradient-to-r:hover {
  opacity: 1;
  transform: scaleY(1.1);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .graph-container {
    transform: scale(0.9);
    transform-origin: top center;
  }
}

@media (max-width: 640px) {
  .graph-container {
    transform: scale(0.8);
    transform-origin: top center;
  }
}

/* Existing styles */
input[type="range"] {
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  width: 100%;
  cursor: pointer;
}
input[type="range"]::-webkit-slider-runnable-track {
  height: 4px;
  border-radius: 2px;
  background: #157813;
}
input[type="range"]:focus::-webkit-slider-runnable-track {
  background: #157813;
}
input[type="range"]::-webkit-slider-thumb {
  -webkit-appearance: none;
  height: 16px;
  width: 16px;
  margin-top: -6px;
  background: #157813;
  border-radius: 50%;
  border: 2px solid white;
  cursor: pointer;
}
input[type="range"]::-moz-range-track {
  height: 4px;
  border-radius: 2px;
  background: #157813;
}
input[type="range"]::-moz-range-thumb {
  height: 16px;
  width: 16px;
  background: #157813;
  border: 2px solid white;
  border-radius: 50%;
  cursor: pointer;
}

/* Tag styling in features/inferred/mentioned sections */
.relative.overflow-hidden.rounded-lg.px-4.py-2 {
  border: 1px solid #ccc; /* default if not overwritten by your function */
}

/* Add these animations to your existing styles */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.8s ease-out forwards;
}

.animate-pulse {
  animation: pulse 2s infinite;
}
</style>